import React, { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../config/api";
import {
  FaHandHoldingHeart,
  FaSearch,
  FaTrash,
  FaEye,
  FaRupeeSign,
  FaUsers,
  FaCalendarAlt,
  FaCheckCircle,
  FaUniversity,
  FaCheck,
  FaTimes,
  FaHourglassHalf,
  FaExternalLinkAlt,
  FaReceipt,
} from "react-icons/fa";
import "../styles/ManageDonation.css";
import DonationReceipt from "../../components/DonationReceipt";
import { showCustomConfirm } from "../../utils/customPopup";

const ManageDonation = () => {
  const API = API_ENDPOINTS.DONATIONS;

  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [receiptDonation, setReceiptDonation] = useState(null);

  useEffect(() => {
    fetchDonations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDonations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      const res = await fetch(API, {
        headers: { Authorization: token },
      });
      const data = await res.json();

      if (res.ok) {
        setDonations(Array.isArray(data) ? data : []);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Fetch donation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/${id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setDonations((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, status: newStatus } : item
          )
        );
        if (selectedDonation && selectedDonation._id === id) {
          setSelectedDonation((prev) => ({ ...prev, status: newStatus }));
        }
        alert(`🎉 Donation status updated to ${newStatus}`);
      } else {
        alert(data.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Update status error:", error);
      alert("Server Error while updating status");
    }
  };

  const handleDelete = (id) => {
    showCustomConfirm({
      title: "Delete Donation Record",
      message: "Are you sure you want to permanently delete this donation record?",
      confirmText: "Delete Record",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("adminToken");
          const res = await fetch(`${API}/${id}`, {
            method: "DELETE",
            headers: { Authorization: token },
          });
          const data = await res.json();

          if (res.ok) {
            setDonations((prev) => prev.filter((item) => item._id !== id));
            if (selectedDonation && selectedDonation._id === id) {
              setSelectedDonation(null);
            }
            alert("🎉 Donation record deleted successfully");
          } else {
            alert(data.message || "Failed to delete donation");
          }
        } catch (error) {
          console.error("Delete donation error:", error);
          alert("Server Error");
        }
      },
    });
  };

  const filteredDonations = donations.filter((item) => {
    const donorName = item.donarName || item.donorName || item.name || "";
    const email = item.email || "";
    const txnId = item.transactionId || "";
    const matchesSearch =
      donorName.toLowerCase().includes(search.toLowerCase()) ||
      email.toLowerCase().includes(search.toLowerCase()) ||
      txnId.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Approved" &&
        (item.status?.toLowerCase() === "approved" ||
          item.status?.toLowerCase() === "success")) ||
      (statusFilter === "Pending" &&
        (!item.status || item.status.toLowerCase() === "pending")) ||
      (statusFilter === "Rejected" &&
        item.status?.toLowerCase() === "rejected");

    return matchesSearch && matchesStatus;
  });

  const totalAmount = donations
    .filter(
      (item) =>
        item.status?.toLowerCase() === "approved" ||
        item.status?.toLowerCase() === "success"
    )
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const pendingCount = donations.filter(
    (item) => !item.status || item.status?.toLowerCase() === "pending"
  ).length;

  const approvedCount = donations.filter(
    (item) =>
      item.status?.toLowerCase() === "approved" ||
      item.status?.toLowerCase() === "success"
  ).length;

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="manage-donation-container">
      {/* BANNER SECTION */}
      <div className="donation-banner">
        <div className="banner-content">
          <h1>🎁 Manage Donations & Payments</h1>
          <p>
            Review donor payments, inspect transaction screenshots, and approve
            or reject manual QR code contributions.
          </p>
        </div>
      </div>

      {/* STATISTICS ROW */}
      <div className="donation-stats">
        <div className="stat-card amt">
          <div className="icon-wrapper">
            <FaRupeeSign />
          </div>
          <div className="stat-details">
            <h3>₹{totalAmount.toLocaleString("en-IN")}</h3>
            <p>Approved Funds Received</p>
          </div>
        </div>

        <div className="stat-card usr">
          <div className="icon-wrapper">
            <FaUsers />
          </div>
          <div className="stat-details">
            <h3>{donations.length}</h3>
            <p>Total Donors Count</p>
          </div>
        </div>

        <div className="stat-card check">
          <div className="icon-wrapper">
            <FaCheckCircle />
          </div>
          <div className="stat-details">
            <h3>{approvedCount}</h3>
            <p>Approved Payments</p>
          </div>
        </div>

        <div className="stat-card pending-card">
          <div className="icon-wrapper">
            <FaHourglassHalf />
          </div>
          <div className="stat-details">
            <h3>{pendingCount}</h3>
            <p>Pending Approvals</p>
          </div>
        </div>
      </div>

      {/* CONTROLS SEARCH & FILTER BAR */}
      <div className="dashboard-controls">
        <div className="search-filter-box">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by donor name, email, or Payment ID (UTR)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="status-filter-pills">
          {["All", "Pending", "Approved", "Rejected"].map((st) => (
            <button
              key={st}
              className={`filter-pill-btn ${
                statusFilter === st ? "active" : ""
              }`}
              onClick={() => setStatusFilter(st)}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT BLOCK */}
      <div className="panel-container">
        {loading ? (
          <div className="donations-loading">
            <div className="spinner"></div>
            <h2>Fetching Donations Registry...</h2>
            <p>Connecting to secure database</p>
          </div>
        ) : filteredDonations.length === 0 ? (
          <div className="empty-state">
            <FaHandHoldingHeart className="empty-icon" />
            <h3>No Donations Found</h3>
            <p>
              {search || statusFilter !== "All"
                ? "Try adjusting your search query or status filter."
                : "No donation records have been registered yet."}
            </p>
          </div>
        ) : (
          <>
            {/* DESKTOP TABLE VIEW */}
            <div className="donation-table-wrapper">
              <table className="donation-table">
                <thead>
                  <tr>
                    <th>Donor Name</th>
                    <th>Email & Phone</th>
                    <th>Amount</th>
                    <th>Purpose</th>
                    <th>Payment ID (UTR)</th>
                    <th>Screenshot</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredDonations.map((donation, index) => (
                    <tr key={donation._id || index}>
                      <td>
                        <div className="donor-info">
                          <FaHandHoldingHeart className="donor-heart-icon" />
                          <span>
                            {donation.donarName ||
                              donation.donorName ||
                              donation.name ||
                              "N/A"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="contact-col">
                          <span>{donation.email || "N/A"}</span>
                          <small>
                            {donation.phoneNumber || donation.mobile || ""}
                          </small>
                        </div>
                      </td>

                      <td className="amount">
                        ₹{Number(donation.amount || 0).toLocaleString("en-IN")}
                      </td>

                      <td>
                        <span className="purpose-tag">
                          {donation.purpose || "General"}
                        </span>
                      </td>

                      <td>
                        <code className="utr-code">
                          {donation.transactionId || "N/A"}
                        </code>
                      </td>

                      <td>
                        {donation.paymentScreenshot ? (
                          <div className="screenshot-thumb-wrapper">
                            <img
                              src={donation.paymentScreenshot}
                              alt="Receipt"
                              className="screenshot-thumb"
                              onClick={() =>
                                setPreviewImage(donation.paymentScreenshot)
                              }
                              title="Click to view full screenshot"
                            />
                          </div>
                        ) : (
                          <span className="no-img-label">No Image</span>
                        )}
                      </td>

                      <td>
                        <span
                          className={`status-pill ${
                            donation.status?.toLowerCase() === "approved" ||
                            donation.status?.toLowerCase() === "success"
                              ? "success"
                              : donation.status?.toLowerCase() === "rejected"
                              ? "rejected"
                              : "pending"
                          }`}
                        >
                          {donation.status || "Pending"}
                        </span>
                      </td>

                      <td>
                        <div className="date-box">
                          <FaCalendarAlt className="mini-icon" />
                          <span>{formatDate(donation.createdAt)}</span>
                        </div>
                      </td>

                      <td>
                        <div className="action-buttons-cell">
                          {donation.status?.toLowerCase() !== "approved" &&
                            donation.status?.toLowerCase() !== "success" && (
                              <button
                                className="approve-action-btn"
                                onClick={() =>
                                  handleStatusUpdate(donation._id, "Approved")
                                }
                                title="Approve Donation"
                              >
                                <FaCheck />
                              </button>
                            )}

                          {donation.status?.toLowerCase() !== "rejected" && (
                            <button
                              className="reject-action-btn"
                              onClick={() =>
                                handleStatusUpdate(donation._id, "Rejected")
                              }
                              title="Reject Donation"
                            >
                              <FaTimes />
                            </button>
                          )}

                          <button
                            className="inspect-btn"
                            onClick={() => setSelectedDonation(donation)}
                            title="Inspect full details"
                          >
                            <FaEye />
                          </button>

                          <button
                            className="receipt-admin-action-btn"
                            onClick={() => setReceiptDonation(donation)}
                            title="Generate / Print Official Mandir Receipt"
                          >
                            <FaReceipt />
                          </button>

                          <button
                            className="trash-delete-btn"
                            onClick={() => handleDelete(donation._id)}
                            title="Delete transaction record"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* RESPONSIVE MOBILE VIEW CARDS */}
            <div className="donations-mobile-grid">
              {filteredDonations.map((donation, index) => (
                <div
                  className="donation-mobile-card"
                  key={donation._id || index}
                >
                  <div className="mobile-card-header">
                    <div className="donor-info">
                      <FaHandHoldingHeart className="donor-heart-icon" />
                      <h4>
                        {donation.donarName ||
                          donation.donorName ||
                          donation.name ||
                          "N/A"}
                      </h4>
                    </div>

                    <span
                      className={`status-pill ${
                        donation.status?.toLowerCase() === "approved" ||
                        donation.status?.toLowerCase() === "success"
                          ? "success"
                          : donation.status?.toLowerCase() === "rejected"
                          ? "rejected"
                          : "pending"
                      }`}
                    >
                      {donation.status || "Pending"}
                    </span>
                  </div>

                  <div className="mobile-card-body">
                    <div className="mobile-amount-row">
                      <span className="amount-label">Amount Paid</span>
                      <span className="amount-val">
                        ₹{Number(donation.amount || 0).toLocaleString("en-IN")}
                      </span>
                    </div>

                    <div className="mobile-detail-rows">
                      <div className="detail-item">
                        <span>Email:</span>
                        <p>{donation.email || "N/A"}</p>
                      </div>
                      <div className="detail-item">
                        <span>Mobile:</span>
                        <p>
                          {donation.phoneNumber || donation.mobile || "N/A"}
                        </p>
                      </div>
                      <div className="detail-item">
                        <span>Payment ID (UTR):</span>
                        <p className="utr-code">
                          {donation.transactionId || "N/A"}
                        </p>
                      </div>
                      <div className="detail-item">
                        <span>Purpose:</span>
                        <p>{donation.purpose || "N/A"}</p>
                      </div>
                      <div className="detail-item">
                        <span>Date:</span>
                        <p>{formatDate(donation.createdAt)}</p>
                      </div>
                    </div>

                    {donation.paymentScreenshot && (
                      <div className="mobile-screenshot-box">
                        <span>Payment Receipt Screenshot:</span>
                        <img
                          src={donation.paymentScreenshot}
                          alt="Receipt"
                          className="mobile-screenshot-img"
                          onClick={() =>
                            setPreviewImage(donation.paymentScreenshot)
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div className="mobile-card-actions">
                    {donation.status?.toLowerCase() !== "approved" &&
                      donation.status?.toLowerCase() !== "success" && (
                        <button
                          className="mobile-action-btn approve"
                          onClick={() =>
                            handleStatusUpdate(donation._id, "Approved")
                          }
                        >
                          <FaCheck /> Approve
                        </button>
                      )}

                    {donation.status?.toLowerCase() !== "rejected" && (
                      <button
                        className="mobile-action-btn reject"
                        onClick={() =>
                          handleStatusUpdate(donation._id, "Rejected")
                        }
                      >
                        <FaTimes /> Reject
                      </button>
                    )}

                    <button
                      className="mobile-action-btn inspect"
                      onClick={() => setSelectedDonation(donation)}
                    >
                      <FaEye /> View
                    </button>
                    <button
                      className="mobile-action-btn delete"
                      onClick={() => handleDelete(donation._id)}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* DETAIL INSPECTION MODAL */}
      {selectedDonation && (
        <div className="donation-modal-overlay">
          <div className="donation-modal">
            <div className="modal-header">
              <h2>
                <FaUniversity className="modal-title-icon" />
                Donation & Payment Inspection
              </h2>
              <button
                className="modal-close"
                onClick={() => setSelectedDonation(null)}
              >
                &times;
              </button>
            </div>

            <div className="modal-content">
              <div className="modal-item">
                <span>Donor Name</span>
                <p>
                  {selectedDonation.donarName ||
                    selectedDonation.donorName ||
                    selectedDonation.name ||
                    "N/A"}
                </p>
              </div>

              <div className="modal-item">
                <span>Email Address</span>
                <p>{selectedDonation.email || "N/A"}</p>
              </div>

              <div className="modal-item">
                <span>Mobile Number</span>
                <p>
                  {selectedDonation.phoneNumber ||
                    selectedDonation.mobile ||
                    "N/A"}
                </p>
              </div>

              <div className="modal-item amount-highlight">
                <span>Amount Contributed</span>
                <p>
                  ₹
                  {Number(selectedDonation.amount || 0).toLocaleString("en-IN")}
                </p>
              </div>

              <div className="modal-item">
                <span>Offering Purpose</span>
                <p>{selectedDonation.purpose || "N/A"}</p>
              </div>

              <div className="modal-item">
                <span>Payment Method</span>
                <p>{selectedDonation.paymentMethod || "QR Code / UPI"}</p>
              </div>

              <div className="modal-item">
                <span>Payment / UTR Number</span>
                <p className="utr-code-lg">
                  {selectedDonation.transactionId || "N/A"}
                </p>
              </div>

              <div className="modal-item">
                <span>Verification Status</span>
                <p>
                  <span
                    className={`status-pill ${
                      selectedDonation.status?.toLowerCase() === "approved" ||
                      selectedDonation.status?.toLowerCase() === "success"
                        ? "success"
                        : selectedDonation.status?.toLowerCase() === "rejected"
                        ? "rejected"
                        : "pending"
                    }`}
                  >
                    {selectedDonation.status || "Pending"}
                  </span>
                </p>
              </div>

              <div className="modal-item">
                <span>Submission Date</span>
                <p>{formatDate(selectedDonation.createdAt)}</p>
              </div>

              {/* PAYMENT SCREENSHOT PREVIEW IN MODAL */}
              {selectedDonation.paymentScreenshot && (
                <div className="modal-item full-width screenshot-modal-block">
                  <span>Uploaded Payment Screenshot</span>
                  <div className="modal-screenshot-frame">
                    <img
                      src={selectedDonation.paymentScreenshot}
                      alt="Payment Receipt"
                      className="modal-screenshot-img"
                      onClick={() =>
                        setPreviewImage(selectedDonation.paymentScreenshot)
                      }
                    />
                    <a
                      href={selectedDonation.paymentScreenshot}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="open-full-btn"
                    >
                      <FaExternalLinkAlt /> Open Original Image
                    </a>
                  </div>
                </div>
              )}

              <div className="modal-item full-width">
                <span>Database Document ID</span>
                <p className="id-code">{selectedDonation._id}</p>
              </div>

              {/* ACTION BUTTONS INSIDE MODAL */}
              <div className="modal-actions-bar full-width">
                {selectedDonation.status?.toLowerCase() !== "approved" &&
                  selectedDonation.status?.toLowerCase() !== "success" && (
                    <button
                      className="modal-approve-btn"
                      onClick={() =>
                        handleStatusUpdate(selectedDonation._id, "Approved")
                      }
                    >
                      <FaCheck /> Approve Payment
                    </button>
                  )}

                {selectedDonation.status?.toLowerCase() !== "rejected" && (
                  <button
                    className="modal-reject-btn"
                    onClick={() =>
                      handleStatusUpdate(selectedDonation._id, "Rejected")
                    }
                  >
                    <FaTimes /> Reject Payment
                  </button>
                )}

                <button
                  className="modal-approve-btn"
                  style={{ background: "#0f2d5c", color: "#f5c542" }}
                  onClick={() => setReceiptDonation(selectedDonation)}
                >
                  <FaReceipt /> Generate Mandir Receipt
                </button>

                <button
                  className="modal-delete-btn"
                  onClick={() => handleDelete(selectedDonation._id)}
                >
                  <FaTrash /> Delete Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IMAGE FULL SCREEN PREVIEW MODAL */}
      {previewImage && (
        <div
          className="image-lightbox-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <div className="image-lightbox-card">
            <button
              className="lightbox-close"
              onClick={() => setPreviewImage(null)}
            >
              &times;
            </button>
            <img
              src={previewImage}
              alt="Full Receipt"
              className="lightbox-img"
            />
          </div>
        </div>
      )}

      {/* MANDIR OFFICIAL DONATION RECEIPT MODAL */}
      <DonationReceipt
        isOpen={Boolean(receiptDonation)}
        donation={receiptDonation}
        onClose={() => setReceiptDonation(null)}
      />
    </div>
  );
};

export default ManageDonation;
